import React, {useCallback, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {ProcessFlowchartNodeSection} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {ApplyFlowchartChangesFn} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/FlowchartStateTypes';
import SearchInput from 'ui-library/SearchInput';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {UserFacingNodeTypes} from '../processFlowchartConfigEditorConstants';
import {FlowchartNode, isInteractivePlaceholderNode} from '../processFlowchartConfigEditorNodeTypes';
import {NODE_TYPE_TO_SECTION, NODE_SECTION_TO_RENDERER} from '../processFlowchartConfigEditorRegistry';
import {FlowchartOptions} from '../processFlowchartConfigEditorUtils';
import {useFlowchartSidepanelManagerContext} from '../right-sidepanel/FlowchartSidepanelUnsavedChangesContext';
import {SidepanelType} from '../right-sidepanel/useFlowchartSidepanelManager';
import {ConfigurableTaskDefinitionGQL} from '../useAllSchemasById';
import {NodeSidepanelSection} from './NodeSidepanelSection';

interface AddNodeSidepanelContentProps {
  allowedNodeTypes: Array<UserFacingNodeTypes>;
  taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>;
  applyFlowchartChanges: ApplyFlowchartChangesFn;
  options: FlowchartOptions;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
  reactFlowWrapper: React.RefObject<HTMLDivElement>;
  setPlaceholderNodeBeingAdded: (value: string | null) => void;
}

/**
 * A sidepanel component that displays all available nodes that can be added to the flowchart
 */
export default function AddNodeSidepanelContent(props: AddNodeSidepanelContentProps): JSX.Element {
  const {applyFlowchartChanges, setPlaceholderNodeBeingAdded} = props;
  const {flowchartTypeName} = props.flowchartEditorConfigurator;
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const sidepanelManager = useFlowchartSidepanelManagerContext();
  const addNodeToCanvas = useCallback(
    async (newNode: FlowchartNode): Promise<void> => {
      const isPlaceholder = isInteractivePlaceholderNode(newNode);
      analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.NODE_ADDED_FROM_LEFT_SIDEPANEL, {
        actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
        nodeType: newNode.data.nodeType,
        flowchartType: flowchartTypeName,
      });

      await sidepanelManager.changeSidepanel({
        sidepanelChange: {
          sidepanel: SidepanelType.NODE_SETTINGS,
          nodeId: newNode.id,
        },
        onBeforeSidepanelChanged: async () => {
          await applyFlowchartChanges(
            () => ({nodeChanges: [{type: 'add', item: newNode}], edgeChanges: []}),
            false,
            isPlaceholder
          );
          if (isPlaceholder) {
            setPlaceholderNodeBeingAdded(newNode.id);
          }
        },
      });
    },
    [applyFlowchartChanges, setPlaceholderNodeBeingAdded, flowchartTypeName, sidepanelManager]
  );

  const nodeSectionsAndTypes = props.allowedNodeTypes.reduce(
    (acc: Map<ProcessFlowchartNodeSection, Array<UserFacingNodeTypes>>, nodeType) => {
      const section = NODE_TYPE_TO_SECTION[nodeType];
      if (!acc.has(section)) {
        acc.set(section, []);
      }
      acc.get(section)!.push(nodeType);
      return acc;
    },
    new Map<ProcessFlowchartNodeSection, Array<UserFacingNodeTypes>>()
  );

  return (
    <>
      <SearchInput
        placeholder="Search"
        doSearch={(searchQuery) => {
          setSearchQuery(searchQuery);
        }}
        query={searchQuery}
      />
      <>
        {/* Render in the order the keys are defined in our registry */}
        {(Object.keys(NODE_SECTION_TO_RENDERER) as Array<keyof typeof NODE_SECTION_TO_RENDERER>).map(
          (section) => {
            const nodeTypes = nodeSectionsAndTypes.get(section);
            if (!nodeTypes) {
              return null;
            }
            return (
              <NodeSidepanelSection
                key={section}
                section={section}
                nodeTypes={nodeTypes}
                taskDefinitionsForSidepanel={props.taskDefinitionsForSidepanel}
                addNodeToCanvas={addNodeToCanvas}
                options={props.options}
                searchQuery={searchQuery}
                rootTaskSchema={props.rootTaskSchema}
                flowchartEditorConfigurator={props.flowchartEditorConfigurator}
                reactFlowWrapper={props.reactFlowWrapper}
              />
            );
          }
        )}
      </>
    </>
  );
}
